import type { AuditNoteEntry, StoreReconciliationAuditShiftModel } from "models";
import { formatShiftRangeLabel } from "./shiftBreakdownUtils";
import { buildShiftDisplayLabelRows } from "./shiftDisplayLabels";

export interface DailyCashierShiftNote {
  key: string;
  text: string;
  authorName: string;
  createdAt: string;
  shiftLabel: string;
  shiftTimeRange: string;
}

interface ShiftCashierNotes {
  auditId: string;
  notes: AuditNoteEntry[];
}

const toText = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
};

const toTimestamp = (value: string) => {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER;
};

export const buildDailyCashierShiftNotes = (
  shiftCashierNotes: ShiftCashierNotes[],
  shifts: StoreReconciliationAuditShiftModel[],
  reportDate: string
): DailyCashierShiftNote[] => {
  const shiftRows = buildShiftDisplayLabelRows(shifts, { resetByReportDate: true });
  const shiftByAuditId = new Map(
    shiftRows.map((row) => [String(row.shift.auditId), row])
  );

  return shiftCashierNotes
    .flatMap((shiftNote, shiftIndex) => {
      const matchedShift = shiftByAuditId.get(shiftNote.auditId);
      const shift = matchedShift?.shift;

      return shiftNote.notes
        .filter((entry) => entry.type !== "system_investigation")
        .map((entry: AuditNoteEntry, entryIndex): DailyCashierShiftNote | null => {
          const text = toText(entry.text);
          if (!text) return null;

          return {
            key: String(entry.id || `${shiftNote.auditId}-${shiftIndex}-${entryIndex}`),
            text,
            authorName: toText(entry.author_name)
              || toText(shift?.userName)
              || toText(shift?.operatorName),
            createdAt: toText(
              entry.created_at_local
              || entry.created_at
              || shift?.reportDatetime
            ),
            shiftLabel: matchedShift?.displayLabel || `Shift ${shiftIndex + 1}`,
            shiftTimeRange: shift ? formatShiftRangeLabel(shift, reportDate) : "",
          };
        })
        .filter((entry): entry is DailyCashierShiftNote => Boolean(entry));
    })
    .sort((a, b) => toTimestamp(a.createdAt) - toTimestamp(b.createdAt));
};
